import { LoopVoice } from './LoopVoice.js';
import { getPanNode, initSpatialPool, isSpatialReady } from './spatial.js';

type OscillatorType = 'sine' | 'square' | 'sawtooth' | 'triangle';

interface SynthSound {
  type: 'synth';
  oscillator: OscillatorType;
  frequency: number;
  duration: number;
  gain: number;
}

interface FileSound {
  type: 'file';
  url: string;
  gain?: number;
  variants?: string[];
}

export type SoundDef = SynthSound | FileSound;
type SoundSource = 'synth' | 'file';

export type WorldPosition = readonly [number, number, number];

export class SoundManager {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private buffers = new Map<string, AudioBuffer>();
  private activeSources: AudioScheduledSourceNode[] = [];
  private source: SoundSource;
  private initialized = false;

  // Looping voice buses — music and ambient share the crossfade logic via LoopVoice.
  private musicVoice = new LoopVoice(800, 0.5, () => this.musicDuckGain ?? this.masterGain);
  private ambientVoice = new LoopVoice(1200, 0.3, () => this.ambientDuckGain ?? this.masterGain);

  // Ducking buses: music and ambient route through these before masterGain,
  // so heavy SFX can temporarily duck them out of the way.
  private musicDuckGain: GainNode | null = null;
  private ambientDuckGain: GainNode | null = null;
  private sfxPeakRef = 0;
  private duckRestoreTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(source: SoundSource = 'synth') {
    this.source = source;
  }

  get isInitialized() {
    return this.initialized;
  }

  init() {
    if (this.initialized) return;
    this.ctx = new AudioContext();
    this.masterGain = this.ctx.createGain();
    // Dev sessions start silent at the engine level — the settings store's
    // dev-mute default can be defeated by any later settings save, and the
    // stored value only reaches this gain when the Settings modal opens.
    this.masterGain.gain.value = import.meta.env.DEV ? 0 : 1;
    this.masterGain.connect(this.ctx.destination);
    // Duck buses sit between music/ambient gains and masterGain so SFX-driven
    // ducking can attenuate them without touching SFX itself.
    this.musicDuckGain = this.ctx.createGain();
    this.musicDuckGain.gain.value = 1;
    this.musicDuckGain.connect(this.masterGain);
    this.ambientDuckGain = this.ctx.createGain();
    this.ambientDuckGain.gain.value = 1;
    this.ambientDuckGain.connect(this.masterGain);
    // Wire up the spatial panner pool — panners route through masterGain so
    // they still respect setMasterVolume().
    initSpatialPool(this.ctx, this.masterGain);
    this.initialized = true;
    // Surface init to E2E so smoke tests can assert audio came up without
    // having to introspect private state.
    if (typeof window !== 'undefined') {
      (window as unknown as { __soundManagerInitialized?: boolean }).__soundManagerInitialized =
        true;
    }
  }

  setMasterVolume(v: number) {
    if (this.masterGain) this.masterGain.gain.value = Math.max(0, Math.min(1, v));
  }

  stopAll() {
    for (const src of this.activeSources) {
      try {
        src.stop();
      } catch {
        /* already stopped */
      }
    }
    this.activeSources.length = 0;
  }

  dispose() {
    this.stopAll();
    if (this.duckRestoreTimer) {
      clearTimeout(this.duckRestoreTimer);
      this.duckRestoreTimer = null;
    }
    if (this.ctx) {
      this.ctx.close().catch(() => {});
      this.ctx = null;
    }
    this.masterGain = null;
    this.musicDuckGain = null;
    this.ambientDuckGain = null;
    this.buffers.clear();
    this.initialized = false;
  }

  play(soundId: string, position?: WorldPosition) {
    if (!this.ctx) return;
    import('./sounds').then(({ getSoundDef }) => {
      const def = getSoundDef(soundId);
      if (!def) return;
      if (def.type === 'synth') this.playSynth(def, position);
      else this.playFile(def, position);
    });
  }

  /**
   * Preload (fetch + decode) the file URLs referenced by the given sound IDs
   * into the buffer cache. Synth-mode and unknown IDs are skipped silently.
   * Errors per-URL are caught and logged to console.warn (don't fail the batch
   * just because one URL 404s).
   */
  async preloadMany(soundIds: readonly string[]): Promise<void> {
    const ctx = this.ctx;
    if (!ctx) return;
    const { getSoundDef } = await import('./sounds');
    const unique = new Set<string>();
    for (const id of soundIds) {
      const def = getSoundDef(id);
      if (!def || def.type !== 'file') continue;
      const urls = def.variants && def.variants.length > 0 ? def.variants : [def.url];
      for (const url of urls) unique.add(url);
    }
    await Promise.all(
      Array.from(unique).map(async (url) => {
        if (this.buffers.has(url)) return;
        try {
          const resp = await fetch(url);
          const buf = await ctx.decodeAudioData(await resp.arrayBuffer());
          this.buffers.set(url, buf);
        } catch (err) {
          console.warn(`[audio] preload failed for ${url}:`, err);
        }
      }),
    );
  }

  /** Play a looping music track, crossfading from the current one. */
  async playMusic(soundId: string, fadeMs = 800) {
    const ctx = this.ctx;
    if (!ctx) return;
    const { getSoundDef } = await import('./sounds.js');
    await this.musicVoice.play(ctx, this.buffers, getSoundDef, soundId, fadeMs);
  }

  /** Stop current music with a fade-out. */
  stopMusic(fadeMs = 500) {
    if (!this.ctx) return;
    this.musicVoice.stop(this.ctx, fadeMs);
  }

  /** Play a looping ambient sound, crossfading from the current one. */
  async playAmbient(soundId: string, fadeMs = 1200) {
    const ctx = this.ctx;
    if (!ctx) return;
    const { getSoundDef } = await import('./sounds.js');
    await this.ambientVoice.play(ctx, this.buffers, getSoundDef, soundId, fadeMs);
  }

  /** Stop current ambient with a fade-out. */
  stopAmbient(fadeMs = 800) {
    if (!this.ctx) return;
    this.ambientVoice.stop(this.ctx, fadeMs);
  }

  private track(src: AudioScheduledSourceNode) {
    this.activeSources.push(src);
    src.addEventListener('ended', () => {
      const i = this.activeSources.indexOf(src);
      if (i !== -1) this.activeSources.splice(i, 1);
    });
  }

  /**
   * Track instantaneous SFX peaks so heavy combat ducks music + ambient out
   * of the way. Latches sfxPeakRef on each loud hit and schedules a single
   * restore timer — if another loud hit lands within 250ms, the latch moves
   * and the restore does nothing.
   */
  private trackSfxPeak(effectiveGain: number) {
    if (effectiveGain < 0.5) return;
    const ctx = this.ctx;
    if (!ctx) return;
    this.sfxPeakRef = performance.now();
    if (this.musicDuckGain) {
      this.musicDuckGain.gain.setTargetAtTime(0.5, ctx.currentTime, 0.05);
    }
    if (this.ambientDuckGain) {
      this.ambientDuckGain.gain.setTargetAtTime(0.3, ctx.currentTime, 0.05);
    }
    if (this.duckRestoreTimer) clearTimeout(this.duckRestoreTimer);
    this.duckRestoreTimer = setTimeout(() => {
      const elapsed = performance.now() - this.sfxPeakRef;
      if (elapsed < 200) return;
      const c = this.ctx;
      if (!c) return;
      if (this.musicDuckGain) {
        this.musicDuckGain.gain.setTargetAtTime(1, c.currentTime, 0.2);
      }
      if (this.ambientDuckGain) {
        this.ambientDuckGain.gain.setTargetAtTime(1, c.currentTime, 0.2);
      }
      this.duckRestoreTimer = null;
    }, 250);
  }

  private playSynth(def: SynthSound, position?: WorldPosition) {
    if (!this.ctx) return;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = def.oscillator;
    osc.frequency.value = def.frequency;
    const now = this.ctx.currentTime;
    gain.gain.setValueAtTime(def.gain, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + def.duration);
    if (position && isSpatialReady()) {
      const pan = getPanNode();
      pan.positionX.value = position[0];
      pan.positionY.value = position[1];
      pan.positionZ.value = position[2];
      osc.connect(gain).connect(pan);
    } else {
      osc.connect(gain).connect(this.masterGain ?? this.ctx.destination);
    }
    this.track(osc);
    osc.start(now);
    osc.stop(now + def.duration);
    this.trackSfxPeak(def.gain);
  }

  private async playFile(def: FileSound, position?: WorldPosition) {
    const ctx = this.ctx;
    if (!ctx) return;
    const variants = def.variants;
    const url =
      variants && variants.length > 0
        ? (variants[Math.floor(Math.random() * variants.length)] as string)
        : def.url;
    let buffer = this.buffers.get(url);
    if (!buffer) {
      try {
        const resp = await fetch(url);
        const arrayBuf = await resp.arrayBuffer();
        buffer = await ctx.decodeAudioData(arrayBuf);
        this.buffers.set(url, buffer);
      } catch {
        return;
      }
    }
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    const gainNode = ctx.createGain();
    const gainValue = def.gain ?? 1.0;
    gainNode.gain.value = gainValue;
    if (position && isSpatialReady()) {
      const pan = getPanNode();
      pan.positionX.value = position[0];
      pan.positionY.value = position[1];
      pan.positionZ.value = position[2];
      source.connect(gainNode).connect(pan);
    } else {
      source.connect(gainNode).connect(this.masterGain ?? ctx.destination);
    }
    this.track(source);
    source.start();
    this.trackSfxPeak(gainValue);
  }
}
